import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import PageHeader from '../common/PageHeader';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import ActionConfirmModal from '../common/ActionConfirmModal';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const paymentMethods = [
  { value: 'normal', label: 'Naqd' },
  { value: 'terminal', label: 'Terminal' },
  { value: 'company', label: 'Tashkilot' },
  { value: 'report', label: 'Hisobot' }
];

const paymentMenuSections = [
  { key: 'debt', label: 'Qarz', accent: 'danger', statLabel: 'joriy summa' },
  { key: 'invalid', label: 'Noaktiv', accent: 'warning', statLabel: 'statuslar' },
  { key: 'history', label: 'Kassa tarixi', accent: 'primary', statLabel: 'operatsiya' },
  { key: 'serviceReport', label: 'Xizmat hisoboti', accent: 'info', statLabel: "to'lov turi" },
  { key: 'queue', label: 'Navbat boshqaruvi', accent: 'success', statLabel: 'tanlovlar' }
];

const initialPaymentForm = {
  amount: '',
  payment_method: 'normal',
  description: '',
  discount_type: '0',
  discount_value: ''
};

const calculateDiscountAmount = (debt, type, value) => {
  const numericDebt = Number(debt || 0);
  const numericValue = Number(value || 0);

  if (Number(type) === 1) {
    return Math.min(numericDebt, (numericDebt * Math.max(numericValue, 0)) / 100);
  }

  if (Number(type) === 2) {
    return Math.min(numericDebt, Math.max(numericValue, 0));
  }

  return 0;
};

const Payments = () => {
  const history = useHistory();
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({ total: 0, amount: 0 });
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [historySearch, setHistorySearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [paymentForm, setPaymentForm] = useState(initialPaymentForm);
  const [isSaving, setIsSaving] = useState(false);
  const [activeCashSection, setActiveCashSection] = useState('history');
  const [isAmountManual, setIsAmountManual] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState(initialPaymentForm);
  const [editPatient, setEditPatient] = useState(null);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [isEditAmountManual, setIsEditAmountManual] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchPayments = async patientId => {
    const params = { search: historySearch, limit: 500 };
    if (patientId) {
      params.patient_id = patientId;
    }

    const res = await axios.get(`${apiBase}/payments.php`, { params });
    if (res.data.success) {
      setPayments(res.data.payments || []);
      setPaymentSummary(res.data.summary || { total: 0, amount: 0 });
    }
  };

  const fetchPatient = async patientId => {
    if (!patientId) {
      setSelectedPatient(null);
      setIsAmountManual(false);
      setPaymentForm(initialPaymentForm);
      return;
    }

    const res = await axios.get(`${apiBase}/patients.php`, { params: { id: patientId } });
    if (res.data.success && res.data.patient) {
      const patient = res.data.patient;
      setSelectedPatient(patient);
      setPatientSearch(patient.full_name || '');
      setIsAmountManual(false);
      setPaymentForm({
        ...initialPaymentForm,
        amount: patient.debt > 0 ? String(patient.debt) : ''
      });
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const patientId = query.get('patient_id');

    if (patientId) {
      fetchPatient(patientId).catch(error => {
        toast.error(error.response?.data?.message || 'Bemor ma\'lumotlari yuklanmadi');
      });
    } else {
      setSelectedPatient(null);
      setPaymentForm(initialPaymentForm);
    }
  }, [location.search]);

  useEffect(() => {
    fetchPayments(selectedPatient?.id).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historySearch, selectedPatient]);

  useEffect(() => {
    const loadPatients = async () => {
      const search = patientSearch.trim();
      const isNumericSearch = /^\d+$/.test(search);
      if ((!isNumericSearch && search.length < 2) || (isNumericSearch && search.length < 1)) {
        setPatientOptions([]);
        return;
      }

      const res = await axios.get(`${apiBase}/patients.php`, {
        params: {
          search,
          limit: 10
        }
      });

      if (res.data.success) {
        setPatientOptions(res.data.patients || []);
      }
    };

    if (selectedPatient && patientSearch === selectedPatient.full_name) {
      setPatientOptions([]);
      return;
    }

    const timeout = setTimeout(() => {
      loadPatients().catch(console.error);
    }, 250);

    return () => clearTimeout(timeout);
  }, [patientSearch, selectedPatient]);

  const selectPatient = patient => {
    history.replace(`/payments?patient_id=${patient.id}`);
    setPatientOptions([]);
  };

  const clearPatient = () => {
    history.replace('/payments');
    setPatientSearch('');
    setPatientOptions([]);
    setSelectedPatient(null);
    setIsAmountManual(false);
    setPaymentForm(initialPaymentForm);
  };

  const applyPatientSnapshot = patient => {
    if (!patient) {
      return;
    }

    setSelectedPatient(current => ({
      ...(current || {}),
      ...patient
    }));

    if (patient.full_name) {
      setPatientSearch(patient.full_name);
    }
  };

  const closeEditModal = () => {
    setEditingPayment(null);
    setEditPatient(null);
    setEditForm(initialPaymentForm);
    setIsEditAmountManual(false);
    setIsEditSaving(false);
  };

  const openEditModal = async payment => {
    try {
      const res = await axios.get(`${apiBase}/patients.php`, { params: { id: payment.patient_id } });
      const patient = res.data?.patient;

      if (!res.data?.success || !patient) {
        throw new Error("Bemor ma'lumotlari yuklanmadi");
      }

      setEditingPayment(payment);
      setEditPatient(patient);
      setIsEditAmountManual(false);
      setEditForm({
        amount: payment.amount ? String(payment.amount) : '',
        payment_method: payment.payment_method || 'normal',
        description: payment.description || '',
        discount_type: String(payment.discount_type || 0),
        discount_value: payment.discount_type ? String(payment.discount_type_value || '') : ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "To'lov ma'lumotlari yuklanmadi");
    }
  };

  const currentDebt = Number(selectedPatient?.debt || 0);
  const discountType = Number(paymentForm.discount_type || 0);
  const discountValue = Number(paymentForm.discount_value || 0);

  const calculatedDiscount = calculateDiscountAmount(currentDebt, discountType, discountValue);

  const discountedDebt = Math.max(0, currentDebt - calculatedDiscount);
  const enteredAmount = Number(paymentForm.amount || 0);
  const projectedDebt = Math.max(0, discountedDebt - Math.max(enteredAmount, 0));

  const editBaseDebt = editingPayment
    ? Number(editPatient?.debt || 0) + Number(editingPayment.amount || 0) + Number(editingPayment.discount || 0)
    : 0;
  const editDiscountType = Number(editForm.discount_type || 0);
  const editDiscountValue = Number(editForm.discount_value || 0);
  const editCalculatedDiscount = calculateDiscountAmount(editBaseDebt, editDiscountType, editDiscountValue);
  const editDiscountedDebt = Math.max(0, editBaseDebt - editCalculatedDiscount);
  const editEnteredAmount = Number(editForm.amount || 0);
  const editProjectedDebt = Math.max(0, editDiscountedDebt - Math.max(editEnteredAmount, 0));

  useEffect(() => {
    if (!selectedPatient || isAmountManual) {
      return;
    }

    const nextAmount = discountedDebt > 0 ? String(discountedDebt) : '';
    setPaymentForm(form => (form.amount === nextAmount ? form : { ...form, amount: nextAmount }));
  }, [discountedDebt, isAmountManual, selectedPatient]);

  useEffect(() => {
    if (!selectedPatient) {
      return;
    }

    const currentAmount = Number(paymentForm.amount || 0);
    if (currentAmount <= 0 || currentAmount <= discountedDebt) {
      return;
    }

    setPaymentForm(form => ({
      ...form,
      amount: discountedDebt > 0 ? String(discountedDebt) : ''
    }));
  }, [discountedDebt, paymentForm.amount, selectedPatient]);

  useEffect(() => {
    if (!editingPayment || !editPatient || isEditAmountManual) {
      return;
    }

    const nextAmount = editDiscountedDebt > 0 ? String(editDiscountedDebt) : '';
    setEditForm(form => (form.amount === nextAmount ? form : { ...form, amount: nextAmount }));
  }, [editDiscountedDebt, editingPayment, editPatient, isEditAmountManual]);

  useEffect(() => {
    if (!editingPayment || !editPatient) {
      return;
    }

    const currentAmountValue = Number(editForm.amount || 0);
    if (currentAmountValue <= 0 || currentAmountValue <= editDiscountedDebt) {
      return;
    }

    setEditForm(form => ({
      ...form,
      amount: editDiscountedDebt > 0 ? String(editDiscountedDebt) : ''
    }));
  }, [editDiscountedDebt, editForm.amount, editingPayment, editPatient]);

  const handlePaymentSubmit = async e => {
    e.preventDefault();

    if (isSaving) {
      return;
    }

    if (!selectedPatient) {
      toast.warning('Avval bemorni tanlang');
      return;
    }

    const amount = Number(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.warning('To\'lov summasini kiriting');
      return;
    }

    if (discountType === 1 && (discountValue <= 0 || discountValue > 100)) {
      toast.warning('Foiz chegirma 1 dan 100 gacha bo\'lishi kerak');
      return;
    }

    if (discountType === 2 && (discountValue <= 0 || discountValue > currentDebt)) {
      toast.warning("Summa chegirma bemor qarzidan katta bo'lmasligi kerak");
      return;
    }

    if (amount > discountedDebt) {
      setPaymentForm(form => ({
        ...form,
        amount: discountedDebt > 0 ? String(discountedDebt) : ''
      }));
      toast.warning("To'lov summasi chegirmaga mos ravishda avtomatik tuzatildi");
      return;
    }

    setIsSaving(true);
    try {
      const res = await axios.post(`${apiBase}/payments.php`, {
        patient_id: selectedPatient.id,
        amount,
        payment_method: paymentForm.payment_method,
        description: paymentForm.description,
        discount_type: discountType,
        discount_value: discountType === 0 ? '' : paymentForm.discount_value
      });

      if (res.data.success) {
        toast.success(res.data.message);
        const nextDebt = Number(res.data.patient?.debt || 0);
        applyPatientSnapshot(res.data.patient);
        setIsAmountManual(false);
        setPaymentForm({
          ...initialPaymentForm,
          amount: nextDebt > 0 ? String(nextDebt) : ''
        });

        const refreshResults = await Promise.allSettled([
          fetchPatient(selectedPatient.id),
          fetchPayments(selectedPatient.id)
        ]);

        if (refreshResults.some(result => result.status === 'rejected')) {
          console.error('Payment refresh failed', refreshResults);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'To\'lov saqlanmadi');
    } finally {
      setIsSaving(false);
    }
  };

  const invalidPayments = payments.filter(item => Number(item.status) !== 1);

  const handleEditSubmit = async e => {
    e.preventDefault();

    if (isEditSaving) {
      return;
    }

    if (!editingPayment || !editPatient) {
      return;
    }

    const amount = Number(editForm.amount);
    if (!amount || amount <= 0) {
      toast.warning("To'lov summasini kiriting");
      return;
    }

    if (editDiscountType === 1 && (editDiscountValue <= 0 || editDiscountValue > 100)) {
      toast.warning("Foiz chegirma 1 dan 100 gacha bo'lishi kerak");
      return;
    }

    if (editDiscountType === 2 && (editDiscountValue <= 0 || editDiscountValue > editBaseDebt)) {
      toast.warning("Summa chegirma bemor qarzidan katta bo'lmasligi kerak");
      return;
    }

    if (amount > editDiscountedDebt) {
      setEditForm(form => ({
        ...form,
        amount: editDiscountedDebt > 0 ? String(editDiscountedDebt) : ''
      }));
      toast.warning("To'lov summasi chegirmaga mos ravishda avtomatik tuzatildi");
      return;
    }

    setIsEditSaving(true);
    try {
      const res = await axios.put(`${apiBase}/payments.php`, {
        id: editingPayment.id,
        amount,
        payment_method: editForm.payment_method,
        description: editForm.description,
        discount_type: editDiscountType,
        discount_value: editDiscountType === 0 ? '' : editForm.discount_value
      });

      if (res.data.success) {
        toast.success(res.data.message);
        if (selectedPatient && selectedPatient.id === editingPayment.patient_id && res.data.patient) {
          applyPatientSnapshot(res.data.patient);
        }

        const refreshResults = await Promise.allSettled([
          selectedPatient && selectedPatient.id === editingPayment.patient_id
            ? fetchPatient(editingPayment.patient_id)
            : Promise.resolve(),
          fetchPayments(selectedPatient?.id)
        ]);

        if (refreshResults.some(result => result.status === 'rejected')) {
          console.error('Payment edit refresh failed', refreshResults);
        }

        closeEditModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "To'lov yangilanmadi");
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const res = await axios.delete(`${apiBase}/payments.php`, {
        data: { id: deleteTarget.id }
      });

      if (res.data.success) {
        toast.success(res.data.message);
        if (selectedPatient && selectedPatient.id === deleteTarget.patient_id) {
          await fetchPatient(deleteTarget.patient_id);
        }
        await fetchPayments(selectedPatient?.id);
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "To'lov o'chirilmadi");
    }
  };

  const paymentMethodStats = paymentMethods
    .map(method => ({
      ...method,
      count: payments.filter(item => item.payment_method === method.value).length,
      amount: payments
        .filter(item => item.payment_method === method.value)
        .reduce((sum, item) => sum + Number(item.amount || 0), 0)
    }))
    .filter(item => item.count > 0);

  const sectionMetrics = {
    debt: Number(selectedPatient?.debt || 0).toLocaleString('ru-RU'),
    invalid: invalidPayments.length,
    history: paymentSummary.total || payments.length,
    serviceReport: paymentMethodStats.length,
    queue: selectedPatient ? 1 : 0
  };

  const filteredPayments = activeCashSection === 'invalid' ? invalidPayments : payments;
  const activeSectionMeta = paymentMenuSections.find(section => section.key === activeCashSection);
  const selectedPatientId = selectedPatient?.id || '';
  const emptyMessage =
    activeCashSection === 'debt' && !selectedPatient
      ? "Qarz bo'limi uchun avval bemorni tanlang."
      : activeCashSection === 'invalid'
        ? "Noaktiv yoki bekor holatdagi to'lovlar topilmadi."
        : "To'lovlar topilmadi.";

  const columns = [
    { dataField: 'id', text: 'ID', sort: true, classes: 'align-middle' },
    { dataField: 'patient_name', text: 'Bemor', sort: true, classes: 'align-middle font-weight-bold', formatter: value => value || '-' },
    {
      dataField: 'amount',
      text: 'Summa',
      sort: true,
      classes: 'align-middle',
      formatter: value => Number(value || 0).toLocaleString('ru-RU')
    },
    {
      dataField: 'discount',
      text: 'Chegirma',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) =>
        Number(value) > 0 ? (
          <Badge color="soft-warning">{item.discount_label || Number(value).toLocaleString('ru-RU')}</Badge>
        ) : (
          '-'
        )
    },
    {
      dataField: 'payment_method',
      text: "To'lov turi",
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color="soft-info">{value || 'normal'}</Badge>
    },
    { dataField: 'description', text: 'Izoh', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'cashier_name', text: 'Kassir', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'date', text: 'Sana', sort: true, classes: 'align-middle' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button color="link" className="p-0 med-action-btn" onClick={() => openEditModal(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            color="link"
            className="p-0 text-danger med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget(item)}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <>
      <PageHeader
        title="To'lovlar"
        description="Eski kassa oqimiga mos: bemorni tanlang, qarzni ko'ring va shu yerning o'zida to'lov qabul qiling."
        className="mb-3"
      />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Kassa bo'limlari"
            summary={`${activeSectionMeta ? activeSectionMeta.label : "Kassa tarixi"} holati tanlangan`}
            actions={
              <>
                <Badge color={activeSectionMeta ? `soft-${activeSectionMeta.accent}` : 'soft-primary'} pill>
                  {activeSectionMeta ? activeSectionMeta.label : "Kassa tarixi"}: {sectionMetrics[activeCashSection]}
                </Badge>
                {activeCashSection !== 'history' && (
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    className="med-patient-filter-reset"
                    onClick={() => setActiveCashSection('history')}
                  >
                    Asosiy holat
                  </Button>
                )}
              </>
            }
            storageKey="payments-sections-collapse"
          >
            <div className="med-patient-filter-grid med-payment-menu-grid">
              {paymentMenuSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeCashSection === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveCashSection(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{sectionMetrics[section.key]}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>

            {activeCashSection === 'serviceReport' && paymentMethodStats.length > 0 && (
              <div className="med-payment-method-strip">
                {paymentMethodStats.map(method => (
                  <div key={method.value} className="med-payment-method-pill">
                    <strong>{method.label}</strong>
                    <span>
                      {method.count} ta, {Number(method.amount).toLocaleString('ru-RU')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm med-dashboard-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="To'lov formasi"
            summary={selectedPatient ? `${selectedPatient.full_name} tanlangan` : "To'lov uchun bemor tanlanmagan"}
            storageKey="payments-entry-collapse"
          >
            <Row>
              <Col lg={5}>
                <FormGroup className="mb-0 position-relative">
                  <Label>Shaxs / F.I.O / ID qidirish</Label>
                  <Input
                    value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)}
                    placeholder="Bemor F.I.O yoki ID yozing"
                    className="bg-200 border-0"
                  />
                  {patientOptions.length > 0 && (
                    <div className="med-payment-search-results">
                      {patientOptions.map(item => (
                        <button
                          key={item.id}
                          type="button"
                          className="med-payment-search-item"
                          onClick={() => selectPatient(item)}
                        >
                          <span className="font-weight-semi-bold">{item.full_name}</span>
                          <span className="text-500 fs--1">
                            ID: {item.id} | Tel: {item.phone || '-'} | Qarz: {Number(item.debt).toLocaleString('ru-RU')}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col lg={7} className="mt-3 mt-lg-0">
                <div className="h-100 med-payment-selected-card">
                  {selectedPatient ? (
                    <div className="d-flex flex-wrap align-items-start justify-content-between">
                      <div>
                        <div className="text-500 fs--1 mb-1">Tanlangan bemor</div>
                        <h5 className="mb-2">{selectedPatient.full_name}</h5>
                        <div className="text-500 fs--1">
                          ID: {selectedPatient.id} | Telefon: {selectedPatient.phone || '-'}
                        </div>
                      </div>
                      <div className="text-lg-right mt-3 mt-lg-0">
                        <Badge color={selectedPatient.debt > 0 ? 'soft-danger' : 'soft-success'} pill className="mb-2">
                          Qarz: {Number(selectedPatient.debt || 0).toLocaleString('ru-RU')}
                        </Badge>
                        <div>
                          <Button color="link" className="p-0" onClick={clearPatient}>
                            Tanlovni tozalash
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-500">To'lov qilish uchun bemorni tanlang.</div>
                  )}
                </div>
              </Col>
            </Row>

            <Form onSubmit={handlePaymentSubmit} className="mt-4">
              <Row form>
                <Col md={3}>
                  <FormGroup>
                    <Label>To'lov summasi</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paymentForm.amount}
                      disabled={!selectedPatient || currentDebt <= 0}
                      onChange={e => {
                        setIsAmountManual(true);
                        setPaymentForm({ ...paymentForm, amount: e.target.value });
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>To'lov turi</Label>
                    <Input
                      type="select"
                      value={paymentForm.payment_method}
                      disabled={!selectedPatient || currentDebt <= 0}
                      onChange={e => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    >
                      {paymentMethods.map(item => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Chegirma turi</Label>
                    <Input
                      type="select"
                      value={paymentForm.discount_type}
                      disabled={!selectedPatient || currentDebt <= 0}
                      onChange={e =>
                        setPaymentForm({
                          ...paymentForm,
                          discount_type: e.target.value,
                          discount_value: e.target.value === '0' ? '' : paymentForm.discount_value
                        })
                      }
                    >
                      <option value="0">Chegirma yo'q</option>
                      <option value="1">Foiz (%)</option>
                      <option value="2">Summa</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Chegirma qiymati</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paymentForm.discount_value}
                      disabled={!selectedPatient || currentDebt <= 0 || paymentForm.discount_type === '0'}
                      placeholder={paymentForm.discount_type === '1' ? 'Masalan 10' : 'Masalan 50000'}
                      onChange={e => setPaymentForm({ ...paymentForm, discount_value: e.target.value })}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row form>
                <Col md={8}>
                  <FormGroup>
                    <Label>Izoh</Label>
                    <Input
                      value={paymentForm.description}
                      disabled={!selectedPatient || currentDebt <= 0}
                      onChange={e => setPaymentForm({ ...paymentForm, description: e.target.value })}
                      placeholder="Ixtiyoriy izoh"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <div className="med-payment-discount-preview">
                    <div className="med-payment-discount-chip">
                      <span>Asl qarz</span>
                      <strong>{currentDebt.toLocaleString('ru-RU')}</strong>
                    </div>
                    <div className="med-payment-discount-chip">
                      <span>Chegirma</span>
                      <strong>{calculatedDiscount.toLocaleString('ru-RU')}</strong>
                    </div>
                    <div className="med-payment-discount-chip">
                      <span>Qoldiq</span>
                      <strong>{projectedDebt.toLocaleString('ru-RU')}</strong>
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <div className="text-500 fs--1 mb-2 mb-md-0">
                  To'lov va chegirma kiritilgandan keyin qarz qayta hisoblanadi. Qoldiq nol bo'lsa xizmatlar yopiladi.
                </div>
                <Button
                  color="primary"
                  type="submit"
                  disabled={!selectedPatient || currentDebt <= 0 || isSaving}
                >
                  <FontAwesomeIcon icon={isSaving ? 'spinner' : 'cash-register'} spin={isSaving} className="mr-2" />
                  To'lovni saqlash
                </Button>
              </div>
            </Form>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Tarix filtrlari"
            summary={historySearch ? `Qidiruv: ${historySearch}` : "To'lovlar tarixi ko'rsatilmoqda"}
            storageKey="payments-history-filters-collapse"
          >
            <Row className="mb-3 align-items-center">
              <Col md="auto" className="mb-3 mb-md-0">
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
              </Col>
              <Col md={4}>
                <Input
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  placeholder="Bemor yoki kassir bo'yicha qidirish"
                  className="bg-200 border-0"
                />
              </Col>
              <Col className="mt-3 mt-md-0 text-md-right">
                {activeCashSection === 'invalid' ? (
                  <Badge color="soft-warning" pill>
                    Faqat noaktiv to'lovlar
                  </Badge>
                ) : selectedPatient ? (
                  <Badge color="soft-primary" pill>
                    Faqat tanlangan bemor to'lovlari
                  </Badge>
                ) : (
                  <Badge color="soft-info" pill>
                    Barcha to'lovlar
                  </Badge>
                )}
              </Col>
            </Row>
          </CollapsibleFilterSection>
          <MedDataTable
            data={filteredPayments}
            columns={columns}
            pageSize={pageSize}
            paginationResetKey={`${activeCashSection}-${historySearch}-${selectedPatientId}`}
            noDataIndication={<div className="med-dashboard-empty">{emptyMessage}</div>}
          />
        </CardBody>
      </Card>

      <Modal isOpen={Boolean(editingPayment)} toggle={closeEditModal} centered>
        <ModalHeader toggle={closeEditModal}>To'lovni tahrirlash</ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>To'lov summasi</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.amount}
                    disabled={!editPatient || editBaseDebt <= 0}
                    onChange={e => {
                      setIsEditAmountManual(true);
                      setEditForm({ ...editForm, amount: e.target.value });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>To'lov turi</Label>
                  <Input
                    type="select"
                    value={editForm.payment_method}
                    disabled={!editPatient || editBaseDebt <= 0}
                    onChange={e => setEditForm({ ...editForm, payment_method: e.target.value })}
                  >
                    {paymentMethods.map(item => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Chegirma turi</Label>
                  <Input
                    type="select"
                    value={editForm.discount_type}
                    disabled={!editPatient || editBaseDebt <= 0}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        discount_type: e.target.value,
                        discount_value: e.target.value === '0' ? '' : editForm.discount_value
                      })
                    }
                  >
                    <option value="0">Chegirma yo'q</option>
                    <option value="1">Foiz (%)</option>
                    <option value="2">Summa</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Chegirma qiymati</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.discount_value}
                    disabled={!editPatient || editBaseDebt <= 0 || editForm.discount_type === '0'}
                    onChange={e => setEditForm({ ...editForm, discount_value: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Izoh</Label>
              <Input
                value={editForm.description}
                disabled={!editPatient || editBaseDebt <= 0}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Ixtiyoriy izoh"
              />
            </FormGroup>
            <div className="med-payment-discount-preview">
              <div className="med-payment-discount-chip">
                <span>Qayta tiklangan qarz</span>
                <strong>{editBaseDebt.toLocaleString('ru-RU')}</strong>
              </div>
              <div className="med-payment-discount-chip">
                <span>Chegirma</span>
                <strong>{editCalculatedDiscount.toLocaleString('ru-RU')}</strong>
              </div>
              <div className="med-payment-discount-chip">
                <span>Qoldiq</span>
                <strong>{editProjectedDebt.toLocaleString('ru-RU')}</strong>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="border-0">
            <Button color="light" onClick={closeEditModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isEditSaving}>
              <FontAwesomeIcon icon={isEditSaving ? 'spinner' : 'save'} spin={isEditSaving} className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="To'lovni o'chirish"
        message={
          deleteTarget
            ? `${deleteTarget.patient_name} uchun ${Number(deleteTarget.amount).toLocaleString('ru-RU')} so'mlik to'lov o'chiriladi.`
            : ''
        }
        confirmLabel="O'chirish"
        confirmColor="danger"
        onConfirm={handleDeletePayment}
      />
    </>
  );
};

export default Payments;
